import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import { format } from 'date-fns';
import Header from '@/components/navigation/Header';
import Footer from '@/components/sections/Footer';
import { usePublishedPosts } from '@/hooks/usePosts';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';

const Blog = () => {
  const { data: posts, isLoading, error } = usePublishedPosts();

  if (error) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <main className="container mx-auto px-4 py-16">
          <h1 className="text-4xl font-bold mb-8">Blog</h1>
          <p className="text-muted-foreground">Sorry, there was an error loading the blog posts.</p>
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <>
      <Helmet>
        <title>Bitcoin & Cryptocurrency Blog | Chief Bitcoin Officer</title>
        <meta 
          name="description" 
          content="Expert insights on Bitcoin, cryptocurrency, and digital asset strategy. Stay informed with the latest analysis from Chief Bitcoin Officer." 
        />
        <meta property="og:title" content="Bitcoin & Cryptocurrency Blog | Chief Bitcoin Officer" />
        <meta property="og:description" content="Expert insights on Bitcoin, cryptocurrency, and digital asset strategy. Stay informed with the latest analysis from Chief Bitcoin Officer." />
        <meta property="og:type" content="website" />
        <link rel="canonical" href="https://chiefbitcoinofficer.com/blog" />
      </Helmet>
      
      <div className="min-h-screen bg-background">
        <Header />
        
        <main className="container mx-auto px-4 py-16">
          <header className="text-center mb-16">
            <h1 className="text-5xl font-bold mb-6 bg-gradient-primary bg-clip-text text-transparent">
              Bitcoin & Crypto Insights
            </h1>
            <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
              Expert analysis, strategic insights, and the latest developments in Bitcoin and cryptocurrency
            </p>
          </header>

          {isLoading ? (
            <div className="grid gap-8 md:grid-cols-2 lg:grid-cols-3">
              {[...Array(6)].map((_, i) => (
                <Card key={i} className="animate-pulse">
                  <CardHeader>
                    <div className="h-6 bg-muted rounded mb-2"></div>
                    <div className="h-4 bg-muted rounded w-3/4"></div>
                  </CardHeader>
                  <CardContent>
                    <div className="h-4 bg-muted rounded mb-2"></div>
                    <div className="h-4 bg-muted rounded mb-2"></div>
                    <div className="h-4 bg-muted rounded w-1/2"></div>
                  </CardContent>
                </Card>
              ))}
            </div>
          ) : posts && posts.length > 0 ? (
            <div className="grid gap-8 md:grid-cols-2 lg:grid-cols-3">
              {posts.map((post) => (
                <Card key={post.id} className="text-only-card hover:shadow-primary/10 transition-all">
                  <CardHeader>
                    <h2 className="text-xl font-semibold mb-2 line-clamp-2">
                      <Link 
                        to={`/blog/${post.slug}`}
                        className="hover:text-primary transition-colors"
                      >
                        {post.title}
                      </Link>
                    </h2>
                    <div className="meta-line">
                      <time className="text-sm text-muted-foreground">
                        {post.publish_date ? format(new Date(post.publish_date), 'MMM dd, yyyy') : 
                         format(new Date(post.created_at), 'MMM dd, yyyy')}
                      </time>
                      {post.profiles?.full_name && (
                        <span className="text-sm text-muted-foreground">
                          by {post.profiles.full_name}
                        </span>
                      )}
                    </div>
                  </CardHeader>
                  
                  <CardContent>
                    {post.excerpt && (
                      <p className="excerpt line-clamp-3 mb-4">
                        {post.excerpt}
                      </p>
                    )}
                    <Link 
                      to={`/blog/${post.slug}`}
                      className="inline-flex items-center text-primary hover:text-primary-light transition-colors font-medium"
                    >
                      Read More →
                    </Link>
                  </CardContent>
                </Card>
              ))}
            </div>
          ) : (
            <div className="text-center py-16">
              <h2 className="text-2xl font-semibold mb-4">No blog posts yet</h2>
              <p className="text-muted-foreground">
                Check back soon for expert insights on Bitcoin and cryptocurrency.
              </p>
            </div>
          )}
        </main>
        
        <Footer />
      </div>
    </>
  );
};

export default Blog;